import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Paper, Tooltip } from "@mui/material";
import {
  Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  deleteInvoice,
  updateInvoiceStatus,
  addInvoiceComment,
  getInvoiceSignedUrls,
  INVOICE_STATUSES,
  formatCurrency,
} from "./Api";
import { InvoiceFormModal } from "./InvoiceFormModal";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";
import { InvoiceForwardModal } from "./InvoiceForwardModal";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const STATUS_LOOKUP = INVOICE_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

const STATUS_VALUES = INVOICE_STATUSES.map((s) => s.value);

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

// Due Date is a fixed business calendar date (stored at noon UTC) — read in UTC so
// it shows the same day everywhere.
const fmtDate = (d) => (d ? moment.utc(d).format("MMM D, YYYY") : "—");
// Submitted On is the real moment of submission — shown in the viewer's local time
// so it matches the day the invoice was actually created.
const fmtSubmitted = (inv) => {
  const d = inv?.createdAt || inv?.invoiceDate;
  return d ? moment(d).format("MMM D, YYYY") : "—";
};

const isOverdue = (inv) =>
  inv.status === "Overdue" ||
  (inv.dueDate &&
    inv.status !== "Paid" &&
    inv.status !== "Declined" &&
    new Date(inv.dueDate) < new Date());

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LOOKUP[status] || STATUS_LOOKUP.Unpaid;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem",
        fontWeight: 700, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const StatusDropdown = ({ invoice, onStatusChange }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const cfg = STATUS_LOOKUP[invoice.status] || STATUS_LOOKUP.Unpaid;

  const positionMenu = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flip the menu above the trigger when there isn't enough room below it
    // (common on small screens / rows near the viewport bottom).
    const menuH = menuRef.current?.offsetHeight || 200;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuH + 12 && r.top > spaceBelow;
    setCoords({
      top: openUp ? Math.max(8, r.top - menuH - 6) : r.bottom + 6,
      left: r.left,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (open) positionMenu();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const choose = async (newStatus) => {
    setOpen(false);
    if (newStatus === invoice.status) return;

    const res = await Swal.fire({
      title: `Change status to "${newStatus}"?`,
      text: "Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;

    setLoading(true);
    try {
      const result = await updateInvoiceStatus(invoice._id, { status: newStatus });
      onStatusChange(invoice._id, result?.data || { ...invoice, status: newStatus });
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "inline-block" }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        disabled={loading}
        onClick={() => setOpen((o) => !o)}
        className="iv-status-trigger"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
          borderRadius: 20, padding: "4px 10px", fontSize: "0.73rem", fontWeight: 700,
          cursor: loading ? "not-allowed" : "pointer", minWidth: 116,
          justifyContent: "space-between", opacity: loading ? 0.7 : 1,
          transition: "box-shadow 0.12s, transform 0.12s",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
          {invoice.status}
        </span>
        {loading ? (
          <Spinner size="sm" style={{ color: cfg.color, width: 11, height: 11 }} />
        ) : (
          <svg width="9" height="5" viewBox="0 0 9 5" fill="none" style={{ transition: "transform 0.12s", transform: open ? "rotate(180deg)" : "none" }}>
            <path d="M1 1L4.5 4L8 1" stroke={cfg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="iv-status-menu"
          style={{
            position: "fixed", top: coords.top, left: coords.left,
            minWidth: Math.max(coords.width, 150), zIndex: 13000,
          }}
        >
          {STATUS_VALUES.map((s) => {
            const sc = STATUS_LOOKUP[s];
            const active = s === invoice.status;
            return (
              <button
                key={s}
                type="button"
                className="iv-status-option"
                onClick={() => choose(s)}
                style={{ background: active ? sc.bg : "#fff" }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: sc.color, flexShrink: 0 }} />
                <span style={{ color: sc.color, fontWeight: 700, fontSize: "0.78rem", flex: 1, textAlign: "left" }}>{s}</span>
                {active && <FeatherIcon icon="check" size={13} color={sc.color} />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const DocumentsPreviewModal = ({ isOpen, toggle, invoice }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [active, setActive] = useState(null);
  const keys = invoice?.attachments || [];

  useEffect(() => {
    if (!isOpen || keys.length === 0) {
      setSignedUrls({});
      setActive(null);
      return;
    }
    (async () => {
      const urls = await getInvoiceSignedUrls(keys);
      setSignedUrls(urls);
      setActive(keys[0]);
    })();
  }, [isOpen, invoice?._id]); // eslint-disable-line

  const activeUrl = active ? signedUrls[active] : null;
  const activeType = active ? getFileTypeFromKey(active) : "unknown";
  const activeName = active ? active.split("/").pop() : "Documents";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>
          <FeatherIcon icon="paperclip" size={15} style={{ marginRight: 6, verticalAlign: "middle" }} />
          {invoice?.title ? `${invoice.title} — Documents` : "Documents"}
        </span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {keys.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", color: "#94A3B8", gap: 10 }}>
            <FeatherIcon icon="file" size={36} />
            <div>No documents attached.</div>
          </div>
        ) : (
          <div style={{ display: "flex", height: "72vh" }}>
            {/* Thumbnail rail */}
            <div style={{
              width: 96, flexShrink: 0, background: "#0B1220", padding: 10,
              display: "flex", flexDirection: "column", gap: 8, overflowY: "auto",
            }}>
              {keys.map((key) => {
                const t = getFileTypeFromKey(key);
                const url = signedUrls[key];
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActive(key)}
                    title={key.split("/").pop()}
                    style={{
                      width: "100%", height: 70, borderRadius: 8, overflow: "hidden",
                      border: isActive ? "2px solid #6366F1" : "2px solid transparent",
                      background: "#1E293B", cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", padding: 0,
                    }}
                  >
                    {t === "image" && url
                      ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <FeatherIcon icon={t === "pdf" ? "file-text" : "file"} size={22} color={t === "pdf" ? "#F87171" : "#94A3B8"} />}
                  </button>
                );
              })}
            </div>
            {/* Active preview */}
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {!activeUrl ? (
                <Spinner style={{ color: "#A5B4FC" }} />
              ) : (
                <Previewer signedUrl={activeUrl} type={activeType} height="72vh" width="100%" />
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {activeUrl && (
          <a
            href={activeUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={activeName}
            className="iv-btn iv-btn--primary"
            style={{ textDecoration: "none" }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

const InvoicesTable = ({
  invoices: initialInvoices,
  loading,
  properties,
  vendors,
  userRole,
  onRefresh,
  filters,
}) => {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardInvoice, setForwardInvoice] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsInvoice, setDocsInvoice] = useState(null);

  useEffect(() => { setInvoices(initialInvoices); }, [initialInvoices]);

  const role = (userRole || "").toLowerCase();
  const canEdit = ["company", "manager", "technical staff", "vendor"].includes(role);
  const canDelete = role === "company";
  // Only company users and auditors may change an invoice's status — everyone
  // else sees a read-only status badge.
  const canChangeStatus = ["company", "auditor"].includes(role);

  const canCreateNew = !!(filters?.propertyId && filters?.buildingName);

  const prefilledPropertyLabel = (() => {
    if (!filters?.propertyId) return "";
    const prop = (properties || []).find((p) => p._id === filters.propertyId);
    return prop ? (prop.title || prop.name || filters.propertyId) : filters.propertyId;
  })();

  const openNew = () => {
    if (!canCreateNew) {
      toast.warning("Select a property and building before creating a new invoice.");
      return;
    }
    if (!vendors || vendors.length === 0) {
      toast.warning("Add at least one vendor (Vendors tab) before creating an invoice.");
      return;
    }
    setSelected(null);
    setIsNew(true);
    setFormOpen(true);
  };

  const openEdit = (invoice, e) => {
    e?.stopPropagation?.();
    setSelected(invoice);
    setIsNew(false);
    setFormOpen(true);
  };

  const openDetail = (invoice) => {
    setDrawerInvoice(invoice);
    setDrawerOpen(true);
  };

  const openDocuments = (invoice, e) => {
    e?.stopPropagation?.();
    setDocsInvoice(invoice);
    setDocsOpen(true);
  };

  const closeForm = (refresh = true) => {
    setFormOpen(false);
    setSelected(null);
    if (refresh) onRefresh?.();
  };

  const openForward = (invoice, e) => {
    e.stopPropagation();
    setForwardInvoice(invoice);
    setForwardOpen(true);
  };

  const handleStatusChange = (id, updated) => {
    setInvoices((prev) => prev.map((r) => (r._id === id ? updated : r)));
    if (drawerInvoice && drawerInvoice._id === id) setDrawerInvoice(updated);
  };

  const handleInlineComment = async (invoice, e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: "Add a comment",
      input: "textarea",
      inputLabel: "Your comment is saved with your name, role and time",
      inputPlaceholder: "Type a note for the team…",
      showCancelButton: true,
      confirmButtonText: "Post",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 460,
      inputValidator: (v) => (!v?.trim() ? "Comment cannot be empty" : undefined),
    });
    if (!res.isConfirmed) return;
    try {
      const result = await addInvoiceComment(invoice._id, { comment: res.value.trim() });
      const newHistory = result?.data?.commentsHistory;
      if (newHistory) {
        const updated = { ...invoice, commentsHistory: newHistory };
        setInvoices((prev) => prev.map((r) => (r._id === invoice._id ? updated : r)));
        if (drawerInvoice && drawerInvoice._id === invoice._id) setDrawerInvoice(updated);
      }
      toast.success("Comment added");
    } catch {
      toast.error("Failed to add comment");
    }
  };

  const handleDelete = async (invoice, e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: "Delete Invoice?",
      html: `<p>Delete invoice <strong>${invoice.title || `#${invoice.invoiceNo}`}</strong>?<br/><small style="color:#64748B">This cannot be undone.</small></p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteInvoice(invoice._id);
      onRefresh?.();
    } catch {
      toast.error("Failed to delete invoice");
    }
  };

  const colSpan = canEdit ? 12 : 11;

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="file-text" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Finance</div>
              <div className="iv-header__title">Invoices</div>
            </div>
            {invoices.length > 0 && (
              <div className="iv-header__badges">
                <span className="iv-header__badge iv-header__badge--records">
                  {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
          <div className="iv-header__actions">
            {canEdit && (
              <Tooltip title={canCreateNew ? "" : "Select a property and building to create a new invoice"} arrow>
                <span>
                  <button
                    type="button"
                    className="iv-btn iv-btn--primary"
                    onClick={openNew}
                    disabled={!canCreateNew}
                    style={{ opacity: canCreateNew ? 1 : 0.5, cursor: canCreateNew ? "pointer" : "not-allowed" }}
                  >
                    <FeatherIcon icon="plus" size={14} />
                    New Invoice
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="iv-tbl" style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 56 }}>S.No</th>
                <th style={{ minWidth: 120 }}>Submitted On</th>
                <th style={{ minWidth: 120 }}>Due Date</th>
                <th style={{ minWidth: 110, textAlign: "left", paddingLeft: 10 }}>Invoice No.</th>
                <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
                <th style={{ minWidth: 130 }}>Category</th>
                <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                <th style={{ minWidth: 110, textAlign: "right" }}>Amount</th>
                <th style={{ minWidth: 140 }}>Status</th>
                <th style={{ minWidth: 110 }}>Comments</th>
                <th style={{ minWidth: 70 }}>Files</th>
                {canEdit && <th className="iv-actions-th" style={{ width: 120 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spinner style={{ color: "#4F46E5" }} />
                  </td>
                </tr>
              )}

              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="iv-empty-cell">
                    <div className="iv-empty" style={{ minHeight: "25vh" }}>
                      <div className="iv-empty__icon-wrap">
                        <FeatherIcon icon="file-text" size={24} color="#4F46E5" />
                      </div>
                      <div className="iv-empty__title">No Invoices Yet</div>
                      <div className="iv-empty__sub">
                        {canEdit
                          ? canCreateNew
                            ? 'Click "New Invoice" to upload your first invoice.'
                            : "Select a property and building, then click \"New Invoice\" to begin."
                          : "No invoices found for the current filters."}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && invoices.map((inv, idx) => {
                const categoryName = inv.category?.name || "—";
                const propertyName = inv.propertyTitle || inv.property?.title || inv.property?.name || "";
                const attachmentCount = inv.attachments?.length || 0;
                const commentsCount = inv.commentsHistory?.length || 0;
                const overdue = isOverdue(inv);

                return (
                  <tr
                    key={inv._id}
                    className="iv-row"
                    onClick={() => openDetail(inv)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.82rem" }}>
                        {idx + 1}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 500 }}>
                        {fmtSubmitted(inv)}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{
                        fontSize: "0.78rem",
                        color: overdue ? "#B91C1C" : "#475569",
                        fontWeight: overdue ? 700 : 500,
                      }}>
                        {fmtDate(inv.dueDate)}{overdue ? " ⚠" : ""}
                      </span>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <span style={{ fontSize: "0.8rem", color: inv.referenceNo ? "#0F172A" : "#CBD5E1" }}>
                        {inv.referenceNo || "—"}
                      </span>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>
                        {inv.vendorName || "—"}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
                        {inv.title || "Untitled"}
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", background: "#EEF2FF", color: "#4338CA",
                        border: "1px solid #C7D2FE", borderRadius: 6,
                        padding: "3px 10px", fontSize: "0.75rem", fontWeight: 600,
                      }}>
                        {categoryName}
                      </span>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.80rem" }}>{propertyName || "—"}</div>
                      <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 1 }}>{inv.property?.buildingName || "—"}</div>
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                      {formatCurrency(inv.amount, inv.currency)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                      {canChangeStatus ? (
                        <StatusDropdown invoice={inv} onStatusChange={handleStatusChange} />
                      ) : (
                        <StatusBadge status={inv.status} />
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {commentsCount > 0 && (
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              <div style={{ maxWidth: 280, padding: "4px 2px", maxHeight: 260, overflowY: "auto" }}>
                                {(inv.commentsHistory || [])
                                  .slice()
                                  .reverse()
                                  .map((c, i) => (
                                    <div
                                      key={c._id || i}
                                      style={{
                                        paddingBottom: 7, marginBottom: 7,
                                        borderBottom: i < commentsCount - 1 ? "1px solid rgba(255,255,255,0.18)" : "none",
                                      }}
                                    >
                                      <div style={{ fontSize: "0.7rem", fontWeight: 700 }}>
                                        {c.author?.displayName || "Unknown"}
                                        {c.author?.role ? (
                                          <span style={{ opacity: 0.75, fontWeight: 500 }}> · {c.author.role}</span>
                                        ) : null}
                                      </div>
                                      <div style={{ fontSize: "0.62rem", opacity: 0.7, marginBottom: 2 }}>
                                        {c.createdAt ? moment(c.createdAt).format("MMM D, YYYY · h:mm A") : ""}
                                      </div>
                                      <div style={{ fontSize: "0.72rem", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                                        {c.comment}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            }
                          >
                            <span className="iv-chip iv-chip--gray" style={{ cursor: "help" }}>
                              <FeatherIcon icon="message-square" size={11} />{commentsCount}
                            </span>
                          </Tooltip>
                        )}
                        {canEdit ? (
                          <Tooltip title="Add comment" arrow>
                            <button
                              type="button"
                              className="iv-ebtn"
                              onClick={(e) => handleInlineComment(inv, e)}
                            >
                              <FeatherIcon icon="plus" size={12} color="#4F46E5" />
                            </button>
                          </Tooltip>
                        ) : (
                          commentsCount === 0 && <span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>
                        )}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                      {attachmentCount > 0 ? (
                        <Tooltip title="Preview documents" arrow>
                          <button
                            type="button"
                            className="iv-chip iv-chip--indigo"
                            onClick={(e) => openDocuments(inv, e)}
                            style={{ border: "1px solid #C7D2FE", cursor: "pointer" }}
                          >
                            <FeatherIcon icon="paperclip" size={11} />{attachmentCount}
                          </button>
                        </Tooltip>
                      ) : (
                        <span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                        <div className="iv-action-group">
                          <Tooltip title="View Details" arrow>
                            <button type="button" className="iv-ebtn" onClick={() => openDetail(inv)}>
                              <FeatherIcon icon="eye" size={13} color="#0284C7" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <button type="button" className="iv-ebtn" onClick={(e) => openEdit(inv, e)}>
                              <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Forward" arrow>
                            <button
                              type="button"
                              className="iv-ebtn"
                              style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
                              onClick={(e) => openForward(inv, e)}
                            >
                              <FeatherIcon icon="send" size={13} color="#059669" />
                            </button>
                          </Tooltip>
                          {canDelete && (
                            <Tooltip title="Delete" arrow>
                              <button type="button" className="iv-dbtn" onClick={(e) => handleDelete(inv, e)}>
                                <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      <InvoiceFormModal
        isOpen={formOpen}
        onClose={closeForm}
        invoiceData={selected}
        isNew={isNew}
        properties={properties}
        vendors={vendors}
        prefilledPropertyId={filters?.propertyId || ""}
        prefilledBuildingName={filters?.buildingName || ""}
        prefilledPropertyLabel={prefilledPropertyLabel}
      />

      <InvoiceDetailDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerInvoice(null); }}
        invoice={drawerInvoice}
        canEdit={canEdit}
        onChange={(updated) => {
          setDrawerInvoice(updated);
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
        }}
        onForward={() => { setForwardInvoice(drawerInvoice); setForwardOpen(true); }}
        onEdit={() => {
          setSelected(drawerInvoice);
          setIsNew(false);
          setFormOpen(true);
        }}
      />

      <InvoiceForwardModal
        isOpen={forwardOpen}
        onClose={() => { setForwardOpen(false); setForwardInvoice(null); }}
        invoice={forwardInvoice}
        onForwardSuccess={(updatedLog) => {
          if (!forwardInvoice) return;
          const updated = { ...forwardInvoice, communicationLog: updatedLog };
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
          if (drawerInvoice && drawerInvoice._id === updated._id) setDrawerInvoice(updated);
        }}
      />

      <DocumentsPreviewModal
        isOpen={docsOpen}
        toggle={() => { setDocsOpen(false); setDocsInvoice(null); }}
        invoice={docsInvoice}
      />
    </>
  );
};

export { InvoicesTable };
